import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X, Paperclip, FloppyDisk, PaperPlaneTilt, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const EMPLOYMENT_TYPES = ["Full-time", "Contract (C2C)", "Contract (W2)", "1099", "Part-time", "Internship"];

const STATUS_CLS = {
  "Draft": "bg-slate-100 text-slate-600",
  "Awaiting Offer Letter": "bg-amber-100 text-amber-700",
  "Submitted": "bg-sky-100 text-sky-700",
  "Pending SDL Approval": "bg-violet-100 text-violet-700",
  "Pending HR Verification": "bg-indigo-100 text-indigo-700",
  "Approved": "bg-emerald-100 text-emerald-700",
  "Accepted": "bg-emerald-100 text-emerald-700",
  "Rejected": "bg-red-100 text-red-700",
};

/**
 * Create-or-complete Offer modal.
 * Opens automatically when interview status flips to Selected — either with an existing draft offer id (offerId)
 * or with pre-fill seed (companyDefault/roleDefault). Emits `onSaved` on close.
 */
export default function CreateOfferModal({
  open, onClose, candidateId, candidateName,
  offerId,                 // optional: id of the auto-drafted offer
  interviewId,             // optional: link back to interview
  companyDefault, roleDefault,
  onSaved,
}) {
  const [offer, setOffer] = useState(null);
  const [form, setForm] = useState({
    company: companyDefault || "",
    job_title: roleDefault || "",
    employment_type: "",
    salary: "",
    joining_date: "",
    notes: "",
    offer_date: new Date().toISOString().slice(0, 10),
  });
  const [offerLetterDoc, setOfferLetterDoc] = useState(null);   // {id, original_filename}
  const [supportingDocs, setSupportingDocs] = useState([]);      // list of {id, original_filename}
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    if (offerId) {
      api.get(`/offers?candidate_id=${candidateId}`).then((r) => {
        const o = r.data.find((x) => x.id === offerId);
        if (o) {
          setOffer(o);
          setForm({
            company: o.company || companyDefault || "",
            job_title: o.job_title || roleDefault || "",
            employment_type: o.employment_type || "",
            salary: o.salary || "",
            joining_date: o.joining_date || "",
            notes: o.notes || "",
            offer_date: o.offer_date || new Date().toISOString().slice(0, 10),
          });
          if (o.offer_letter_document_id) hydrateDoc(o.offer_letter_document_id, setOfferLetterDoc);
          if (o.supporting_proof_document_ids?.length) hydrateManyDocs(o.supporting_proof_document_ids, setSupportingDocs);
        }
      });
    } else {
      setOffer(null);
      setForm({ company: companyDefault || "", job_title: roleDefault || "", employment_type: "", salary: "", joining_date: "", notes: "", offer_date: new Date().toISOString().slice(0, 10) });
      setOfferLetterDoc(null);
      setSupportingDocs([]);
    }
  }, [open, offerId, candidateId, companyDefault, roleDefault]);

  const hydrateDoc = async (id, setter) => {
    try {
      const r = await api.get(`/documents?candidate_id=${candidateId}`);
      const d = r.data.find((x) => x.id === id);
      if (d) setter({ id: d.id, original_filename: d.original_filename });
    } catch {}
  };
  const hydrateManyDocs = async (ids, setter) => {
    try {
      const r = await api.get(`/documents?candidate_id=${candidateId}`);
      const list = r.data.filter((x) => ids.includes(x.id))
                        .map((d) => ({ id: d.id, original_filename: d.original_filename }));
      setter(list);
    } catch {}
  };

  const uploadDocument = async (file, docType) => {
    const fd = new FormData();
    fd.append("candidate_id", candidateId);
    fd.append("doc_type", docType);
    fd.append("file", file);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/upload`, {
      method: "POST", body: fd, credentials: "include",
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || "Upload failed");
    }
    return await res.json();
  };

  const onOfferLetterChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("letter");
    try {
      const doc = await uploadDocument(file, "offer_letter");
      setOfferLetterDoc({ id: doc.id, original_filename: doc.original_filename });
      toast.success("Offer letter uploaded");
    } catch (ex) { toast.error(ex.message); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const onSupportingChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading("support");
    try {
      const uploaded = [];
      for (const f of files) {
        const d = await uploadDocument(f, "offer_supporting");
        uploaded.push({ id: d.id, original_filename: d.original_filename });
      }
      setSupportingDocs((s) => [...s, ...uploaded]);
      toast.success(`${uploaded.length} supporting document(s) uploaded`);
    } catch (ex) { toast.error(ex.message); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const buildPayload = (statusOverride) => ({
    company: form.company, job_title: form.job_title,
    employment_type: form.employment_type || null,
    salary: form.salary || null,
    joining_date: form.joining_date || null,
    offer_date: form.offer_date,
    notes: form.notes || null,
    offer_letter_document_id: offerLetterDoc?.id || null,
    supporting_proof_document_ids: supportingDocs.map((d) => d.id),
    ...(statusOverride ? { status: statusOverride } : {}),
  });

  const persist = async (statusOverride) => {
    if (!form.company.trim() || !form.job_title.trim()) {
      toast.error("Client name and Job title are required");
      return null;
    }
    setBusy("save");
    try {
      let saved;
      const payload = buildPayload(statusOverride);
      if (offer?.id) {
        const r = await api.put(`/offers/${offer.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/offers", { ...payload, candidate_id: candidateId, interview_id: interviewId || null,
                                              status: statusOverride || (offerLetterDoc ? "Draft" : "Awaiting Offer Letter") });
        saved = r.data;
      }
      setOffer(saved);
      return saved;
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); return null; }
    finally { setBusy(""); }
  };

  const saveDraft = async () => {
    const status = offerLetterDoc ? "Draft" : "Awaiting Offer Letter";
    const s = await persist(status);
    if (s) {
      toast.success(`Draft saved (${s.status})`);
      onSaved?.(s);
    }
  };

  const submitOffer = async () => {
    if (!offerLetterDoc) {
      toast.error("Offer Letter is required before you can submit.");
      return;
    }
    const saved = await persist();       // Save latest edits
    if (!saved) return;
    setBusy("submit");
    try {
      const r = await api.post(`/offers/${saved.id}/submit`);
      toast.success("Offer submitted for SDL approval");
      setOffer(r.data);
      onSaved?.(r.data);
      onClose?.();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const removeSupportingDoc = (id) => setSupportingDocs((s) => s.filter((d) => d.id !== id));

  if (!open) return null;

  const status = offer?.status || (offerLetterDoc ? "Draft" : "Awaiting Offer Letter");
  const canSubmit = !!offerLetterDoc && !["Pending SDL Approval", "Pending HR Verification", "Approved", "Accepted"].includes(status);
  const readonly = ["Pending SDL Approval", "Pending HR Verification", "Approved", "Accepted", "Rejected"].includes(status);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
      <div className="bg-white border border-slate-200 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" data-testid="create-offer-modal">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="font-semibold">{offerId ? "Complete Offer" : "Create Offer"}</h3>
            <div className="text-xs text-slate-500 mt-0.5">Candidate: <b>{candidateName}</b></div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_CLS[status] || STATUS_CLS.Draft}`} data-testid="offer-status-badge">{status}</span>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3 border-b border-slate-100">
          <Field label="Client name *">
            <input required value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                   data-testid="offer-company" disabled={readonly}
                   className="input" />
          </Field>
          <Field label="Job title / Position *">
            <input required value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                   data-testid="offer-job-title" disabled={readonly}
                   className="input" />
          </Field>
          <Field label="Employment type">
            <select value={form.employment_type} onChange={(e) => setForm((f) => ({ ...f, employment_type: e.target.value }))}
                    data-testid="offer-employment-type" disabled={readonly}
                    className="input">
              <option value="">— select —</option>
              {EMPLOYMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Salary / Pay rate (optional)">
            <input value={form.salary} onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))}
                   data-testid="offer-salary" disabled={readonly}
                   className="input" placeholder="e.g. $120,000/yr or $65/hr" />
          </Field>
          <Field label="Offer date">
            <input type="date" value={form.offer_date} onChange={(e) => setForm((f) => ({ ...f, offer_date: e.target.value }))}
                   data-testid="offer-date" disabled={readonly}
                   className="input" />
          </Field>
          <Field label="Joining date (if known)">
            <input type="date" value={form.joining_date} onChange={(e) => setForm((f) => ({ ...f, joining_date: e.target.value }))}
                   data-testid="offer-joining-date" disabled={readonly}
                   className="input" />
          </Field>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Remarks / Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      data-testid="offer-notes" disabled={readonly}
                      className="input w-full" placeholder="Anything else the SDL/HR should know…" />
          </div>
        </div>

        {/* Offer Letter — mandatory */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <Paperclip size={14} /> Offer Letter <span className="text-red-500">*</span>
            </div>
            {!offerLetterDoc && <span className="text-xs text-amber-700 flex items-center gap-1"><WarningCircle size={12} weight="fill" /> required before submit</span>}
          </div>
          {offerLetterDoc ? (
            <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 text-sm">
              <span className="inline-flex items-center gap-1.5 text-emerald-800"><CheckCircle size={14} weight="fill" /> {offerLetterDoc.original_filename}</span>
              {!readonly && <button onClick={() => setOfferLetterDoc(null)} className="text-xs text-slate-500 hover:text-red-600">Replace</button>}
            </div>
          ) : (
            <label className={`inline-flex items-center gap-1.5 border-2 border-dashed border-slate-200 rounded-md px-4 py-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50 ${readonly ? "opacity-50 pointer-events-none" : ""}`}>
              <Paperclip size={14} /> {uploading === "letter" ? "Uploading…" : "Click to upload PDF, DOC, DOCX or image"}
              <input type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={onOfferLetterChange}
                     data-testid="offer-letter-upload" />
            </label>
          )}
        </div>

        {/* Supporting Docs — optional */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Paperclip size={14} /> Supporting documents (optional)</div>
            <span className="text-xs text-slate-500">Client emails, screenshots, references…</span>
          </div>
          {supportingDocs.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {supportingDocs.map((d) => (
                <span key={d.id} className="text-xs bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 inline-flex items-center gap-1" data-testid={`support-doc-${d.id}`}>
                  {d.original_filename}
                  {!readonly && <button onClick={() => removeSupportingDoc(d.id)} className="text-slate-400 hover:text-red-600 ml-1">×</button>}
                </span>
              ))}
            </div>
          )}
          {!readonly && (
            <label className="inline-flex items-center gap-1.5 border border-slate-200 rounded-md px-3 py-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
              <Paperclip size={12} /> {uploading === "support" ? "Uploading…" : "Add supporting document(s)"}
              <input type="file" multiple className="hidden" onChange={onSupportingChange} data-testid="offer-supporting-upload" />
            </label>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 flex flex-wrap items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Close</button>
          {!readonly && (
            <>
              <button onClick={saveDraft} disabled={!!busy} data-testid="offer-save-draft"
                      className="text-sm px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
                <FloppyDisk size={14} /> {busy === "save" ? "Saving…" : "Save Draft"}
              </button>
              <button onClick={submitOffer} disabled={!canSubmit || !!busy} data-testid="offer-submit"
                      className="text-sm px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white inline-flex items-center gap-1">
                <PaperPlaneTilt size={14} /> {busy === "submit" ? "Submitting…" : "Submit Offer"}
              </button>
            </>
          )}
        </div>
      </div>
      <style>{`.input{border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;font-size:14px;background:#fff;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="text-xs text-slate-500 space-y-1 flex flex-col">
      <span>{label}</span>
      {children}
    </label>
  );
}
